import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (App-Brain fleet graduation). askBrain sends
// the user's message + this app's declared action catalog to the generic fleet chat brain
// (POST /api/apps/oriora_chores/chat) — ONE billed call returns ONE registered action + a
// reply; this file resolves any name→id references locally against the ACTIVE member (ChatCtx
// — chores + completions are per-member) and the app executes the action on-device. The
// original on-device regex router below (routeMessage) is kept as the FREE FALLBACK for
// offline / signed-out / out-of-funds — chat never goes dead.
//
// HOUSEHOLD-CHORE FENCE (observational-only — enforced server-side in the weekly-recap prompt,
// reinforced here at the routing layer): the "summarize" action's description below is
// written to keep the routing model itself from ever treating this as a judge/advise action —
// it recaps logged facts only, never judges the member, never recommends reward/punishment,
// never gives parenting advice.

import { store, cadenceLabel, type Chore, type ChorePatch, type Member } from "../store";
import { BrainError, brainCall } from "../brain";
import { findNamedMatches } from "./fuzzy";
import { fenceCheck } from "./fence";
import type { ChatAction, ChatCtx, ChoreQuery } from "./actions";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn { role: "user" | "assistant"; content: string; rows?: import("./actions").FilterRow[]; }
export interface BrainResponse {
  reply: string;         // conversational preface; the executor supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope` (server truncates ~400 chars → hard limits FIRST).
// The household-chore fence lives in the summarize prompt + action description; this closes the
// conversational REPLY channel too, so a parenting-advice / judge-the-member ask is declined.
const SCOPE =
  "ALWAYS help the user record. This app tracks chores for ANYONE the user manages — themselves, a partner, a kid, any household member; the people are just names the user adds. Freely add/log/edit/search chores for whoever the context names, and NEVER refuse a legitimate request to add, log, edit, or delete a chore. " +
  "Give an OBSERVATIONAL weekly recap of a person's OWN logged completions (counts, points, most/least-logged — facts only). " +
  "The ONLY things to decline, plainly: judging a person or calling a chore count good/bad/enough, recommending a reward or punishment, or giving parenting/discipline/personal advice. Everything else about recording chores: just do it. Back up to the user's own cloud.";

const HELP =
  'Tell me and I\'ll do it — e.g. “add chore feed the dog, 2 points, daily”, “log dishes done”, ' +
  '“rename dishes to wash up”, “set homework to 5 points”, “retire trash”, “show overdue chores”, or “summary”.';

// Query verbs that mean "search / list the chores", not "change something" (offline fallback only).
const QUERY_RE = /\b(show|find|list|search|which|what|filter|overdue|due|daily|weekly|once|retired|active|how\s+many)\b/i;

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

/** Find chores for the active person by name — fuzzy (exact → contains → token → typo-tolerant),
 *  Pattern 6 forgiving resolution. Active chores first. */
async function findChores(term: string, ctx: ChatCtx): Promise<Chore[]> {
  const q = term.trim().replace(/[.?!]+$/, "").replace(/^(the|a|an)\s+/i, "");
  if (!q || !ctx.memberId) return [];
  const all = await store.listChores(ctx.memberId);
  const hits = findNamedMatches(all, q);
  return hits.sort((a, b) => (b.active - a.active));
}

/** Parse "feed the dog, 2 points daily" → { name, points, cadence } with sensible defaults (offline fallback only). */
function parseChoreSpec(raw: string): { name: string; points: number; cadence: string } {
  let s = raw.trim();
  let points = 1;      // matches ChoreForm default
  let cadence = "once"; // matches ChoreForm default

  const cadM = s.match(/\b(daily|weekly|monthly|once|one[-\s]?off|every\s*day|every\s*week)\b/i);
  if (cadM) {
    const c = cadM[1].toLowerCase();
    cadence = /one[-\s]?off/.test(c) ? "once" : /every\s*day/.test(c) ? "daily" : /every\s*week/.test(c) ? "weekly" : c;
    s = s.replace(cadM[0], " ");
  }
  const ptM = s.match(/(\d+)\s*(?:pts?|points?|pt)\b/i) || s.match(/\bfor\s+(\d+)\b/i) || s.match(/\bworth\s+(\d+)\b/i);
  if (ptM) { points = Math.max(0, parseInt(ptM[1], 10)); s = s.replace(ptM[0], " "); }

  const name = s.replace(/[,;]+/g, " ").replace(/\b(for|worth|every|at)\b/gi, " ").replace(/\s+/g, " ").trim();
  return { name, points, cadence };
}

// Standing instructions (fleet standard): the customer shapes the app through chat; stored
// on-device, injected into every brain call.
const INSTRUCTIONS_KEY = "oriora.chores.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

// ── SERVER CHAT-BRAIN (the graduated path) ──────────────────────────────────────
const CADENCES = new Set(["daily", "weekly", "monthly", "once"]);

export const CHAT_ACTIONS = [
  { name: "add_chore", description: "Create a new chore for the active person (whoever is selected — the user, a partner, a kid).", params: { name: "the chore's name", points: "points earned per completion (default 1)", cadence: "how often: daily, weekly, monthly, or once (default once)", rotate_between: "optional comma-separated names of the people it should ROTATE between, in turn order (e.g. \"Alex, Sam\") — use when the chore should take turns rather than belong to one person" } },
  { name: "log_done", description: "Log that a chore was completed today, identified by name.", params: { name: "which chore — name as the user wrote it" } },
  { name: "edit_chore", description: "Change an existing chore's name, points, active/retired status, or WHO IT ROTATES BETWEEN. Give at least one field to change. Use rotate_between when the user wants a chore to take turns between people (\"make the bins rotate between Alex and Sam\", \"Sam and I should swap the recycling\") — logging it done then passes the turn to the next person automatically.", params: { name: "which chore — its CURRENT name", new_name: "optional new name, only if renaming", points: "optional new points value, only if changing it", active: "optional true to reactivate / false to retire, only if changing it", rotate_between: "optional comma-separated names of the people it should rotate between, in turn order (e.g. \"Alex, Sam\"); give ONE name to stop it rotating and pin it to that person" } },
  { name: "delete_chore", description: "Delete a chore and its logged completions, identified by name (the app asks the user to confirm first).", params: { name: "which chore — name as the user wrote it" } },
  { name: "add_member", description: "Add a new person to track chores for (the user, a partner, a kid, any household member — the people are just names the user adds).", params: { name: "the person's name" } },
  { name: "list_members", description: "List the people set up in the app.", params: {} },
  { name: "edit_member", description: "Rename a person. Defaults to the active person if none is named.", params: { target: "which person — their current name; omit for the active one", new_name: "the new name" } },
  { name: "delete_member", description: "Delete a person AND all of their chores and logged completions (the app asks the user to confirm first).", params: { target: "which person — their name" } },
  { name: "filter", description: "Search / list the active person's chores.", params: { text: "optional free text to match chore names", cadence: "optional: daily, weekly, monthly, once", only_due: "optional true to show only due/overdue chores", only_retired: "optional true to show only retired chores", only_active: "optional true to show only active chores" } },
  {
    name: "summarize",
    description:
      "The weekly recap: an OBSERVATIONAL-ONLY narration of the active person's logged chores this week (counts, points earned, most/least-logged chore). Recaps logged facts ONLY — never judges the person, never says a count is good/bad/enough, never recommends a reward or punishment, and never gives personal or parenting advice. If the user is asking for a judgment or advice (e.g. \"should I reward them?\", \"are they lazy?\"), do not use this action.",
    params: {},
  },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "save_to_cloud", description: "Save a note or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a note.", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, ctx: ChatCtx, onChunk?: (t: string) => void): Promise<BrainResponse> {
  let list = "(none yet)";
  if (ctx.memberId) {
    const chores = await store.listChores(ctx.memberId);
    const named = chores.slice(0, 40).map((c) => `${c.name} (${c.points}pt, ${cadenceLabel(c.cadence)}${c.active ? "" : ", retired"})`);
    if (named.length) list = named.join(", ");
  }
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Active member: ${ctx.memberName ?? "(none picked)"}. Chores: ${list}.`) +
    (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE }, onChunk);
  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" }, ctx);
    actions.push(one.action);
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asInt = (v: unknown, fallback: number): number => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : fallback;
};
const asBool = (v: unknown): boolean | undefined => {
  if (typeof v === "boolean") return v;
  const s = asStr(v)?.toLowerCase();
  if (s === "true" || s === "yes") return true;
  if (s === "false" || s === "no") return false;
  return undefined;
};

export async function toAction(r: ServerChat, ctx: ChatCtx): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_chore": {
      const name = asStr(p.name);
      if (!name) return none(say || "What's the chore called? e.g. “add chore feed the dog, 2 points, daily”.");
      const points = asInt(p.points, 1);
      const cadenceRaw = asStr(p.cadence)?.toLowerCase();
      const cadence = cadenceRaw && CADENCES.has(cadenceRaw) ? cadenceRaw : "once";
      // Rotation at creation time — resolve the names to ids here, same matcher as edit_chore.
      let rotation: string | undefined;
      let turnMemberId: string | undefined;
      const rotAdd = asStr(p.rotate_between);
      if (rotAdd) {
        const wanted = rotAdd.split(",").map((x) => x.trim()).filter(Boolean);
        const members = await store.listMembers();
        const ids: string[] = [];
        const missing: string[] = [];
        for (const w of wanted) {
          const hit = members.find((m) => m.name.toLowerCase() === w.toLowerCase())
            ?? members.find((m) => m.name.toLowerCase().startsWith(w.toLowerCase()));
          if (hit && !ids.includes(hit.id)) ids.push(hit.id);
          else if (!hit) missing.push(w);
        }
        if (missing.length) return none(`I don't have anyone called ${missing.map((m) => `“${m}”`).join(" or ")} yet — add them first.`);
        if (ids.length >= 2) { rotation = ids.join(","); turnMemberId = ids[0]; }
        else if (ids.length === 1) { turnMemberId = ids[0]; }  // one name = just assign it, no rotation
      }
      return { reply: say, action: { kind: "add_chore", name, points, cadence, rotation, turnMemberId } };
    }
    case "log_done": {
      const term = asStr(p.name);
      if (!term) return none(say || "Which chore is done?");
      const hits = await findChores(term, ctx);
      if (hits.length === 1) return { reply: say, action: { kind: "log_done", choreId: hits[0].id, label: hits[0].name } };
      if (hits.length === 0) return none(`Couldn't find a chore like “${term}”.`);
      return none(`Which one? ${hits.map((h) => `“${h.name}”`).join(", ")}`);
    }
    case "edit_chore": {
      const term = asStr(p.name);
      if (!term) return none(say || "Which chore do you want to change?");
      const hits = await findChores(term, ctx);
      if (hits.length === 0) return none(`Couldn't find a chore like “${term}”.`);
      if (hits.length > 1) return none(`Which one? ${hits.map((h) => `“${h.name}”`).join(", ")}`);
      const patch: ChorePatch = {};
      const newName = asStr(p.new_name);
      if (newName) patch.name = newName;
      if (p.points !== undefined) patch.points = asInt(p.points, hits[0].points);
      const active = asBool(p.active);
      if (active !== undefined) patch.active = active ? 1 : 0;

      // Rotation — Flatastic's duty roster, settable in a sentence instead of a form.
      // ChorePatch already carries `rotation` + `member_id`, so nothing downstream changes:
      // `rotation` is the ordered pool, `member_id` is whose turn it is NOW.
      const rot = asStr(p.rotate_between);
      if (rot) {
        const wanted = rot.split(",").map((x) => x.trim()).filter(Boolean);
        const members = await store.listMembers();
        const ids: string[] = [];
        const missing: string[] = [];
        for (const w of wanted) {
          const hit = members.find((m) => m.name.toLowerCase() === w.toLowerCase())
            ?? members.find((m) => m.name.toLowerCase().startsWith(w.toLowerCase()));
          if (hit && !ids.includes(hit.id)) ids.push(hit.id);
          else if (!hit) missing.push(w);
        }
        if (missing.length) return none(`I don't have anyone called ${missing.map((m) => `“${m}”`).join(" or ")} yet — add them first.`);
        if (ids.length === 0) return none("Who should it rotate between?");
        // One name = stop rotating and pin it to that person; the board then shows a plain owner.
        patch.rotation = ids.length >= 2 ? ids.join(",") : "";
        patch.member_id = ids[0];
      }

      if (Object.keys(patch).length === 0) return none(say || "What should I change — the name, points, who it rotates between, or active status?");
      return { reply: say, action: { kind: "edit_chore", id: hits[0].id, patch, label: hits[0].name } };
    }
    case "delete_chore": {
      const term = asStr(p.name);
      if (!term) return none(say || "Which chore do you want to delete?");
      const hits = await findChores(term, ctx);
      if (hits.length === 1) return { reply: say, action: { kind: "delete_chore", id: hits[0].id, label: hits[0].name } };
      if (hits.length === 0) return none(`Couldn't find a chore like “${term}”.`);
      return none(`Which one? Be a bit more specific: ${hits.map((h) => `“${h.name}”`).join(", ")}`);
    }
    case "add_member": {
      const name = asStr(p.name);
      if (!name) return none(say || "What's the person's name?");
      return { reply: say, action: { kind: "add_member", name } };
    }
    case "list_members":
      return { reply: say, action: { kind: "list_members" } };
    case "edit_member": {
      const res = await resolveMember(asStr(p.target) ?? ctx.memberName ?? undefined);
      if (res.problem) return none(res.problem);
      const newName = asStr(p.new_name);
      if (!newName) return none(say || "What should I rename them to?");
      return { reply: say, action: { kind: "edit_member", id: res.member!.id, name: newName, label: res.member!.name } };
    }
    case "delete_member": {
      const res = await resolveMember(asStr(p.target));
      if (res.problem) return none(res.problem);
      return { reply: say, action: { kind: "delete_member", id: res.member!.id, label: res.member!.name } };
    }
    case "filter": {
      const query: ChoreQuery = {};
      const text = asStr(p.text);
      if (text) query.text = text;
      const cad = asStr(p.cadence)?.toLowerCase();
      if (cad && CADENCES.has(cad)) query.cadence = cad;
      if (asBool(p.only_due)) query.onlyDue = true;
      if (asBool(p.only_retired)) query.onlyRetired = true;
      else if (asBool(p.only_active)) query.onlyActive = true;
      return { reply: "", action: { kind: "filter", query, label: say || undefined } };
    }
    case "summarize":
      return { reply: say, action: { kind: "summarize" } };
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function askBrain(history: ChatTurn[], ctx: ChatCtx, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = none(HELP); return { reply: r.reply, actions: [r.action] }; }

  // 🔴 THE FENCE RUNS PRE-LLM, HERE — before any billed call. It used to be prompt-only (a SCOPE
  // string ASKING the model to decline), and §5c already proved prompting does not hold: an
  // explicit instruction was ignored by the very next generation. Now the refusal is code, so it
  // cannot be talked around and it costs nothing. `fence-battery.mjs` proves it at 0 leaks /
  // 0 over-blocks over 53 probes — over-blocking counts as a failure there, because an app that
  // refuses "how many chores last week?" is broken, not safe.
  const fenced = fenceCheck(text);
  if (fenced.refuse) return { reply: "", actions: [{ kind: "none", message: fenced.message }] };

  try {
    return await serverChat(history, text, ctx, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below still
    // answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text, ctx);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- OFFLINE / OUT-OF-FUNDS FALLBACK (free, on-device, no network) --------------
export async function routeMessage(text: string, ctx: ChatCtx): Promise<One> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  // Same fence on the OFFLINE path — otherwise losing the network would open the gate.
  const fenced = fenceCheck(t);
  if (fenced.refuse) return { reply: "", action: { kind: "none", message: fenced.message } };
  const low = t.toLowerCase();

  // 1) Add a chore
  const addM = t.match(/^(?:add|new|create)\s+(?:a\s+)?(?:chore\s*)?[:\-\s]+([\s\S]+)$/i);
  if (addM) {
    const spec = parseChoreSpec(addM[1]);
    if (!spec.name) return { reply: "", action: { kind: "none", message: "What's the chore called? e.g. “add chore feed the dog, 2 points, daily”." } };
    return { reply: "", action: { kind: "add_chore", ...spec } };
  }

  // 2) Log a chore done (a completion)
  const logM = t.match(/^(?:log|mark|did|finished|completed?|done)\s+([\s\S]+)$/i);
  if (logM) {
    const term = logM[1].replace(/\b(as\s+)?done\b|\bcompleted?\b|✓/gi, "").trim();
    const hits = await findChores(term, ctx);
    if (hits.length === 1) return { reply: "", action: { kind: "log_done", choreId: hits[0].id, label: hits[0].name } };
    if (hits.length === 0) return { reply: "", action: { kind: "none", message: `Couldn't find a chore like “${term}”.` } };
    return { reply: "", action: { kind: "none", message: `Which one? ${hits.map((h) => `“${h.name}”`).join(", ")}` } };
  }

  // 3) Summary / recap (the live AI endpoint)
  if (/\b(summar(?:y|ise|ize)|recap|overview|weekly|how'?s?\s+.+\s+doing)\b/i.test(low)) {
    return { reply: "", action: { kind: "summarize" } };
  }

  // 4) Rename a chore
  const renameM = t.match(/^(?:rename|call)\s+([\s\S]+?)\s+(?:to|as)\s+([\s\S]+)$/i);
  if (renameM) {
    const hits = await findChores(renameM[1], ctx);
    if (hits.length === 1) return { reply: "", action: { kind: "edit_chore", id: hits[0].id, patch: { name: renameM[2].trim() }, label: hits[0].name } };
    if (hits.length === 0) return { reply: "", action: { kind: "none", message: `Couldn't find a chore like “${renameM[1].trim()}”.` } };
    return { reply: "", action: { kind: "none", message: `Which one? ${hits.map((h) => `“${h.name}”`).join(", ")}` } };
  }

  // 5) Change points ("set X to N points", "X is now N points")
  const ptsM = t.match(/^(?:set|change|make)\s+([\s\S]+?)\s+(?:to|=)\s+(\d+)\s*(?:pts?|points?)?\b/i)
    || t.match(/^([\s\S]+?)\s+(?:is|to)\s+now\s+(\d+)\s*(?:pts?|points?)\b/i);
  if (ptsM) {
    const hits = await findChores(ptsM[1], ctx);
    const pts = Math.max(0, parseInt(ptsM[2], 10));
    if (hits.length === 1) return { reply: "", action: { kind: "edit_chore", id: hits[0].id, patch: { points: pts }, label: hits[0].name } };
    if (hits.length === 0) return { reply: "", action: { kind: "none", message: `Couldn't find a chore like “${ptsM[1].trim()}”.` } };
    return { reply: "", action: { kind: "none", message: `Which one? ${hits.map((h) => `“${h.name}”`).join(", ")}` } };
  }

  // 6) Retire / restore a chore (active flag)
  const retM = t.match(/^(retire|archive|restore|reactivate|activate)\s+(?:the\s+)?(?:chore\s+)?([\s\S]+)$/i);
  if (retM) {
    const active = /^(restore|reactivate|activate)$/i.test(retM[1]) ? 1 : 0;
    const hits = await findChores(retM[2], ctx);
    if (hits.length === 1) return { reply: "", action: { kind: "edit_chore", id: hits[0].id, patch: { active }, label: hits[0].name } };
    if (hits.length === 0) return { reply: "", action: { kind: "none", message: `Couldn't find a chore like “${retM[2].trim()}”.` } };
    return { reply: "", action: { kind: "none", message: `Which one? ${hits.map((h) => `“${h.name}”`).join(", ")}` } };
  }

  // 7) Delete a chore (HITL — confirmed in Chat.tsx before it runs)
  const delM = t.match(/^(?:delete|remove)\s+(?:the\s+)?(?:chore\s+)?([\s\S]+)$/i);
  if (delM) {
    const hits = await findChores(delM[1], ctx);
    if (hits.length === 1) return { reply: "", action: { kind: "delete_chore", id: hits[0].id, label: hits[0].name } };
    if (hits.length === 0) return { reply: "", action: { kind: "none", message: `Couldn't find a chore like “${delM[1].trim()}”.` } };
    return { reply: "", action: { kind: "none", message: `Which one? Be a bit more specific: ${hits.map((h) => `“${h.name}”`).join(", ")}` } };
  }

  // 8) Filter / search the chores
  if (QUERY_RE.test(low)) {
    const query: ChoreQuery = {};
    if (/\boverdue\b|\bdue\b/i.test(low)) query.onlyDue = true;
    if (/\bretired\b|\barchived\b/i.test(low)) query.onlyRetired = true;
    else if (/\bactive\b/i.test(low)) query.onlyActive = true;
    const cadM = low.match(/\b(daily|weekly|once|monthly)\b/i);
    if (cadM) query.cadence = cadM[1];
    return { reply: "", action: { kind: "filter", query, label: t } };
  }

  return { reply: "", action: { kind: "none", message: HELP } };
}

// Resolve a person reference to exactly one member, or explain why not (fuzzy, fleet standard).
async function resolveMember(term: string | undefined): Promise<{ member?: Member; problem?: string }> {
  const all = await store.listMembers();
  if (!all.length) return { problem: "You haven't added anyone yet — say e.g. “add person Alex”." };
  const q = term?.trim().replace(/[.?!]+$/, "");
  if (!q) return { problem: `Who do you mean? You have: ${all.map((m) => m.name).join(", ")}.` };
  const matches = findNamedMatches(all, q);
  if (!matches.length) return { problem: `I don't have anyone called “${q}”.` };
  if (matches.length > 1) return { problem: `Which one? ${matches.map((m) => `“${m.name}”`).join(", ")}` };
  return { member: matches[0] };
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
